import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Dimensions } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { FontAwesome5, MaterialIcons, Entypo, MaterialCommunityIcons } from '@expo/vector-icons';
import { FormatDate } from '../../../../../helpers/validation';
import { navigate } from '../../../../../navigation/RootNavigation';
import Dialog from '@material-ui/core/Dialog';
import DialogButton from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import ajax from '../../../../../helpers/ajaxHelper';
import { showErrorToast, showSuccesToast } from '../../helpers';
import Urls from '../../../../../services/Urls';
import getPlatformType from '../../../../../helpers/helpers';

const { width } = Dimensions.get('window');
const isSmallScreen = width < 375;
const CIRCLE_RADIUS = isSmallScreen ? 25 : 30;
const STROKE_WIDTH = 6;
const CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
const layout = getPlatformType();

// Color palette
const COLORS = {
  primary: '#6C5CE7',       // Purple
  secondary: '#00B894',     // Green
  accent: '#FD79A8',        // Pink
  success: '#00B894',       // Green
  warning: '#FDCB6E',       // Yellow
  danger: '#D63031',        // Red
  info: '#0984E3',         // Blue
  light: '#F5F6FA',         // Light gray
  dark: '#2D3436',          // Dark gray
  text: '#2D3436',          // Primary text
  textSecondary: '#636E72', // Secondary text
  border: '#DFE6E9',        // Border color
};

export default function PropertyCard(props) {
  let flow = props?.item;
  const progress = flow?.percentage_completed || 1;

  const getPropertyName = (property) => {
    return `${property?.display_address ? property?.display_address : "-"}`;
  };

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnArchiveModal, setShowUnArchiveModal] = useState(false);
  const [showCompleteWarningModal, setShowCompleteWarningModal] = useState(false);
  const [showRenewWarningModal, setshowRenewWarningModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const strokeDashoffset = CIRCUMFERENCE * (1 - progress / 100);

  const iconItems = [
    {
      icon: <FontAwesome5 name="user" size={18} color={COLORS.text} />,
      label: "Tenants",
      id: 'tenancy_status',
    },
    {
      id: 'doc_status',
      icon: <MaterialCommunityIcons name="file-document-outline" size={18} color={COLORS.text} />,
      label: "Landlords"
    },
    {
      id: 'rental_offer_status',
      icon: <MaterialCommunityIcons name="calendar-check" size={18} color={COLORS.text} />,
      label: "Offer"
    },
    {
      id: 'holding_deposit_status',
      icon: <MaterialIcons name="qr-code-scanner" size={18} color={COLORS.text} />,
      label: "Reference"
    },
    {
      id: 'verification_status',
      icon: <MaterialIcons name="gesture" size={18} color={COLORS.text} />,
      label: "Agreement"
    },
    {
      id: 'landlord_status',
      icon: <MaterialIcons name="photo-camera" size={18} color={COLORS.text} />,
      label: "MoveIn"
    },
    {
      id: 'move_in_status',
      icon: <MaterialCommunityIcons name="currency-gbp" size={18} color={COLORS.text} />,
      label: "Deposit"
    },
    {
      id: 'property_status',
      icon: <MaterialCommunityIcons name="shield-check-outline" size={18} color={COLORS.text} />,
      label: "Protection"
    },
  ];

  const getLinkColor = (status) => {
    const status1 = flow[status];

    if (status1) {
      if (status1 === 0) return COLORS.textSecondary; // Not started
      if (status1 === 1) return COLORS.success;      // Completed
      if (status1 === 4) return COLORS.info;         // In progress
      if (status1 === 5) return COLORS.danger;       // Action required
    }
    return COLORS.textSecondary; // Default
  };

  const steps = [
    'tenancy_status',
    'doc_status',
    'rental_offer_status',
    'holding_deposit_status',
    'verification_status',
    'landlord_status',
    'move_in_status',
    'property_status'
  ];

  const handleCompleteClick = () => {
    const inCompleteSteps = steps.filter((step) => flow[step] !== 1);
    if (inCompleteSteps.length > 0) {
      setShowCompleteWarningModal(true);
    } else {
      setShowCompleteModal(true);
    }
  };

  return (
    <View style={styles.card}>
      {/* Header Section */}
      <View style={styles.header}>
        <View style={styles.addressBlock}>
          <Text style={styles.addressText} numberOfLines={2}>
            {getPropertyName(flow?.property)}
          </Text>
          <View style={styles.metaContainer}>
            <View style={styles.metaItem}>
              <MaterialIcons name="person" size={14} color={COLORS.textSecondary} />
              <Text style={styles.metaText}>
                {flow?.agent ? `${flow.agent.user.first_name} ${flow.agent.user.last_name}` : 'N/A'}
              </Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialIcons name="date-range" size={14} color={COLORS.textSecondary} />
              <Text style={styles.metaText}>
                {flow.open_flow_rental_offer?.tenancy_start_date
                  ? FormatDate(flow.open_flow_rental_offer.tenancy_start_date)
                  : 'N/A'}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.progressContainer}>
          <Svg height={CIRCLE_RADIUS * 2 + STROKE_WIDTH} width={CIRCLE_RADIUS * 2 + STROKE_WIDTH}>
            <Circle
              cx={CIRCLE_RADIUS + STROKE_WIDTH / 2}
              cy={CIRCLE_RADIUS + STROKE_WIDTH / 2}
              r={CIRCLE_RADIUS}
              stroke={COLORS.light}
              strokeWidth={STROKE_WIDTH}
              fill="none"
            />
            <Circle
              cx={CIRCLE_RADIUS + STROKE_WIDTH / 2}
              cy={CIRCLE_RADIUS + STROKE_WIDTH / 2}
              r={CIRCLE_RADIUS}
              stroke={COLORS.primary}
              strokeWidth={STROKE_WIDTH}
              fill="none"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${CIRCLE_RADIUS + STROKE_WIDTH / 2} ${CIRCLE_RADIUS + STROKE_WIDTH / 2})`}
            />
          </Svg>
          <Text style={styles.progressText}>
            {progress}%
          </Text>
        </View>
      </View>

      {/* Parties Section */}
      <View style={styles.partiesSection}>
        <View style={styles.partyBlock}>
          <View style={styles.partyHeader}>
            <FontAwesome5 name="users" size={16} color={COLORS.primary} />
            <Text style={styles.partyLabel}>Lead Tenant</Text>
          </View>
          {flow?.tenants?.filter(tenant => tenant.is_lead).map((tenant, index) => (
            <Text key={index} style={styles.partyName} numberOfLines={1}>
              {tenant?.app_user?.user?.first_name} {tenant?.app_user?.user?.last_name}
            </Text>
          ))}
          {flow?.tenants?.filter(tenant => tenant.is_lead).length === 0 && (
            <Text style={styles.partyName}>N/A</Text>
          )}
        </View>

        <View style={styles.divider} />

        <View style={styles.partyBlock}>
          <View style={styles.partyHeader}>
            <MaterialCommunityIcons name="hand-heart" size={16} color={COLORS.primary} />
            <Text style={styles.partyLabel}>Lead Landlord</Text>
          </View>
          {flow?.open_flow_landlords?.filter(landlord => landlord.is_lead).map((landlord, index) => (
            <Text key={index} style={styles.partyName} numberOfLines={1}>
              {landlord?.first_name} {landlord?.last_name}
            </Text>
          ))}
          {flow?.open_flow_landlords?.filter(landlord => landlord?.is_lead).length === 0 && (
            <Text style={styles.partyName}>N/A</Text>
          )}
        </View>
      </View>

      {/* Progress Icons Grid */}
      <View style={styles.iconGrid}>
        {iconItems.map((item, index) => (
          <View key={index} style={styles.iconContainer}>
            <IconItem
              icon={item.icon}
              label={item.label}
              highlight={getLinkColor(item.id)}
            />
          </View>
        ))}
      </View>

      {/* Action Buttons */}
      <View style={styles.actionButtons}>
        <View style={styles.topActions}>
          {flow?.is_completed ? null : <ActionButton
            onPress={() => setShowConfirmDialog(true)}
            icon="delete"
            label="Delete"
            color={COLORS.danger}
          />}

          {flow?.is_archived ? (
            <ActionButton
              onPress={() => setShowUnArchiveModal(true)}
              icon="unarchive"
              label="Unarchive"
              color={COLORS.info}
            />
          ) : (
            <ActionButton
              onPress={() => setShowArchiveModal(true)}
              icon="archive"
              label="Archive"
              color={COLORS.info}
            />
          )}

          {!flow?.is_completed && (
            <ActionButton
              onPress={handleCompleteClick}
              icon="check-circle"
              label="Complete"
              color={COLORS.success}
            />
          )}
        </View>

        {/* Main CTA Button */}
        {flow?.is_completed ? (
          flow?.draft_agreement_type?.toLowerCase() == "nha" ? <View style={styles.completedActions}>
            <TouchableOpacity
              onPress={() => setshowRenewWarningModal(true)}
              style={[styles.ctaButton, styles.renewButton]}
            >
              <Text style={styles.ctaButtonText}>
                Renew <MaterialCommunityIcons name="autorenew" size={16} color="white" />
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => navigate('TeanantOnboarding', { id: flow?.openflow_id })}
              style={[styles.ctaButton, styles.viewButton]}
            >
              <Text style={styles.ctaButtonText}>
                VIEW <Entypo name="arrow-right" size={16} color="white" />
              </Text>
            </TouchableOpacity>
          </View> : <TouchableOpacity
            onPress={() => navigate('TeanantOnboarding', { id: flow?.openflow_id })}
            style={[styles.ctaButton, styles.viewButton]}
          >
            <Text style={styles.ctaButtonText}>
              VIEW <Entypo name="arrow-right" size={16} color="white" />
            </Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            onPress={() => navigate('TeanantOnboarding', { id: flow?.openflow_id })}
            style={[styles.ctaButton, { backgroundColor: COLORS.primary }]}
          >
            <Text style={styles.ctaButtonText}>
              CONTINUE <Entypo name="arrow-right" size={16} color="white" />
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Dialogs */}
      <CompleteDialog
        open={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        flow={flow}
        onRefresh={props?.onRefresh}
      />

      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Delete flow?"
        message="Are you sure you want to delete this flow? This action cannot be undone."
        onConfirm={() => {
          ajax.delete(Urls.START_FLOW, { params: { flow_id: flow?.openflow_id } })
            .then(({ data }) => {
              setShowConfirmDialog(false);
              if (data.status) {
                props?.onRefresh?.();
                showSuccesToast("Flow deleted successfully");
              } else {
                showErrorToast(data.message || "Failed to delete flow");
              }
            })
            .catch(() => showErrorToast("Failed to delete flow"));
        }}
      />

      <ConfirmationDialog
        open={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        title="Archive Property?"
        message="Are you sure you want to archive this flow?"
        onConfirm={() => {
          ajax.post(`${Urls.archive}?flow_id=${flow.openflow_id}`, {})
            .then(({ data }) => {
              setShowArchiveModal(false);
              if (data.status) {
                props?.onRefresh?.();
                showSuccesToast("Flow archived successfully");
              } else {
                showErrorToast(data.message || "Failed to archive flow");
              }
            })
            .catch(() => showErrorToast("Failed to archive flow"));
        }}
      />

      <ConfirmationDialog
        open={showUnArchiveModal}
        onClose={() => setShowUnArchiveModal(false)}
        title="Unarchive Property?"
        message="Are you sure you want to unarchive this flow?"
        onConfirm={() => {
          ajax.post(`${Urls.unarchive}?flow_id=${flow.openflow_id}`, {})
            .then(({ data }) => {
              setShowUnArchiveModal(false);
              if (data.status) {
                showSuccesToast("Flow unarchived successfully");
                props?.onRefresh?.();
              } else {
                showErrorToast(data.message || "Failed to unarchive flow");
              }
            })
            .catch(() => showErrorToast("Failed to unarchive flow"));
        }}
      />

      <ConfirmationDialog
        open={showCompleteWarningModal}
        onClose={() => setShowCompleteWarningModal(false)}
        title="Incomplete Steps"
        message="There are incomplete steps in this flow."

      />

      <ConfirmationDialog
        open={showRenewWarningModal}
        onClose={() => setshowRenewWarningModal(false)}
        title="Renew Property?"
        message="Are you sure you want to renew this flow?"
        onConfirm={() => {
          ajax.post(`${Urls.renew}`, { openflow_id: flow.openflow_id })
            .then(({ data }) => {
              setshowRenewWarningModal(false);
              if (data.status) {
                showSuccesToast("Flow renewed successfully");
                navigate('TeanantOnboarding', { id: data?.data?.openflow_id });
                props?.onRefresh?.();
              } else {
                showErrorToast(data.message || "Failed to renew flow");
              }
            })
            .catch(() => showErrorToast("Failed to renew flow"));
        }}
      />
    </View>
  );
}

// Dialog Components
const CompleteDialog = ({ open, onClose, flow, onRefresh }) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
  >
    <DialogTitle id="alert-dialog-title">Complete Property Flow</DialogTitle>
    <DialogContent>
      <DialogContentText id="alert-dialog-description">
        Are you sure you want to complete this onboarding?
      </DialogContentText>
      <DialogContentText style={{ marginTop: 8 }}>
        If you select "YES", the flow will be moved to the "completed onboards" section.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <DialogButton onClick={onClose} color="primary">
        No
      </DialogButton>
      <DialogButton onClick={() => {
        ajax.post(`${Urls.complete}?flow_id=${flow.openflow_id}`, {})
          .then(({ data }) => {
            onClose();
            if (data.status) {
              onRefresh?.();
              showSuccesToast("Request completed successfully");
            } else {
              showErrorToast(data.message || "Failed to complete flow");
            }
          })
          .catch(() => showErrorToast("Failed to complete flow"));
      }} color="primary" autoFocus>
        Yes
      </DialogButton>
    </DialogActions>
  </Dialog>
);

const ConfirmationDialog = ({ open, onClose, title, message, onConfirm }) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
  >
    <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
    <DialogContent>
      <DialogContentText id="alert-dialog-description">
        {message}
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <DialogButton onClick={onClose} color="primary">
        Cancel
      </DialogButton>
      {onConfirm ? <DialogButton onClick={onConfirm} color="primary" autoFocus>
        Confirm
      </DialogButton> : null}
    </DialogActions>
  </Dialog>
);

// UI Components
const IconItem = ({ icon, label, highlight }) => (
  <View style={styles.iconItem}>
    {React.cloneElement(icon, { color: highlight })}
    <Text style={[styles.iconLabel, { color: highlight }]}>{label}</Text>
  </View>
);

const ActionButton = ({ icon, label, color, onPress }) => (
  <TouchableOpacity onPress={onPress} style={styles.actionButton}>
    <MaterialIcons name={icon} size={20} color={color} />
    <Text style={[styles.actionLabel, { color }]}>{label}</Text>
  </TouchableOpacity>
);

// Styles
const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 3,
    width: layout !== "phone" ? '48%' : '100%',
    marginHorizontal: isSmallScreen ? 0 : '1%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  addressBlock: {
    flex: 1,
    paddingRight: 8,
  },
  addressText: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.primary,
    marginBottom: 8,
  },
  metaContainer: {
    flexDirection: 'column',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  metaText: {
    fontSize: 12,
    color: COLORS.textSecondary,
    marginLeft: 6,
  },
  progressContainer: {
    width: CIRCLE_RADIUS * 2 + STROKE_WIDTH,
    height: CIRCLE_RADIUS * 2 + STROKE_WIDTH,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  progressText: {
    position: 'absolute',
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.primary,
  },
  partiesSection: {
    flexDirection: 'row',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: COLORS.border,
    marginVertical: 8,
  },
  partyBlock: {
    flex: 1,
  },
  partyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  partyLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.text,
    marginLeft: 6,
  },
  partyName: {
    fontSize: 14,
    color: COLORS.textSecondary,
    marginLeft: 22, // icon width + margin
  },
  divider: {
    width: 1,
    backgroundColor: COLORS.border,
    marginHorizontal: 12,
  },
  iconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  iconContainer: {
    width: layout == "phone" ? '45%' : '25%',
    paddingVertical: 8,
  },
  iconItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  iconLabel: {
    fontSize: 12,
    marginLeft: 6,
    fontWeight: '500',
  },
  actionButtons: {
    marginTop: 8,
  },
  topActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  actionButton: {
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  actionLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 4,
  },
  ctaButton: {
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  ctaButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  completedActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  renewButton: {
    backgroundColor: COLORS.accent,
    flex: 1,
    marginRight: 8,
  },
  viewButton: {
    backgroundColor: COLORS.secondary,
    flex: 1,
  },
});

export { COLORS };